import {
	ConsumptionElement,
	NotificationSubscription,
	Object,
	SavingProgramCase,
	UsageDay,
	User,
} from '@vae/server/dist/client'
import { ConsumptionAnalyzis } from '@vae/server/src/ConsumptionAnalyzer/consumptionAnalyzer.interface'
import {
	ConsumptionData,
	GroupedConsumptionData,
} from '@vae/server/src/interfaces/consumptionData.interface'
import {
	DHAgreement,
	DHCustomerData,
} from '@vae/server/src/modules/ExternalAPIs/DataHub/types/datahub.interface'
import {
	SynerallContact,
	SynerallContract,
	SynerallUser,
} from '@vae/server/src/modules/ExternalAPIs/Synerall/synerallTypes'
import { VaeExchangePrice } from '@vae/server/src/modules/dataAbstractions/types/exchangePrice.interface'
import axiosInstance from '../authentication/axiosInstance'

type HttpMethod = 'get' | 'put' | 'post' | 'delete'

export interface ApiError {
	error: boolean
	message: string
}
const baseURL = process.env.react_app_rest_url || process.env.REACT_APP_REST_URL

export const APICall = async <T = any>(
	url: string,
	method?: HttpMethod,
	body?: any
): Promise<T> => {
	const result = await axiosInstance<T>({
		baseURL,
		url,
		method,
		data: body,
	})

	return result.data
}

export type EndPoint =
	| 'consumptionElements'
	| 'exchange'
	| 'user'
	| 'synerall'
	| 'objects'
	| 'notifications'
	| 'alerts'

export const backendCall = {
	add: (endpoint: EndPoint, object: any): Promise<any> => APICall(`v2/${endpoint}`, 'post', object),

	getById: (endpoint: EndPoint, id: number | string): Promise<any> => APICall(`${endpoint}/${id}`),

	getByUserId: (endpoint: EndPoint, campaignId: number): Promise<any> =>
		APICall(`user/${campaignId}/${endpoint}`),

	edit: (endpoint: EndPoint, id: number | string, object: any): Promise<any> =>
		APICall(`${endpoint}/${id}`, 'put', object),

	delete: (endpoint: EndPoint, id: number) => APICall(`${endpoint}/${id}`, 'delete'),

	getExchangePrices: (startPoint: string, endPoint: string, objectId?: number) =>
		APICall<VaeExchangePrice[]>(`exchange`, 'post', { startPoint, endPoint, objectId }),

	authenticateTeliaToken: (code: string) =>
		APICall<{
			personalId: string
			user: User | null
			synerallUser: SynerallUser
			firstNames: string
			surname: string
		}>('v2/user/authenticate/telia', 'post', {
			code,
		}),

	requiredVersion: () => APICall<{ minVersion: string }>(`user/requiredVersion`),

	users: {
		login: (email: string, password: string) =>
			APICall<User>('v2/user/login', 'post', { email, password }),
		getCurrentUser: () =>
			APICall<{ user: User; synerallUser: SynerallUser | null }>('v2/user/current'),
		getSessionToken: (user: User) => APICall<string>('v2/user/session-token', 'post', { user }),
		getDatahubCustomer: (personalId: string) =>
			APICall<DHCustomerData>(`v2/user/datahub/${personalId}`),
		checkEmailAvailability: (email: string) => APICall<boolean>(`user/emailcheck/${email}`),
		editContact: (id: string, editedFields: any) =>
			APICall<SynerallContact>(`user/contact/${id}`, 'put', editedFields),
		changePassword: (id: number, currentPassword: string, newPassword: string) =>
			APICall<User>(`v2/user/${id}/changePassword`, 'put', {
				currentPassword,
				newPassword,
			}),
		restorePassword: (email: string) =>
			APICall<boolean>(`v2/user/restorepassword`, 'put', { email }),
		deleteAllData: (id: number) => APICall<string>(`v2/user/${id}/deleteAllData`, 'delete'),
	},
	objects: {
		consumptionElements: {
			getAll: (objectId: number | number) =>
				APICall<ConsumptionElement[]>(`consumptionElements/${objectId}`),
			add: (newElement: any) =>
				APICall<ConsumptionElement>(`consumptionElements`, 'post', newElement),
			edit: (elementId: string | number, body: any) =>
				APICall<ConsumptionElement>(`consumptionElements/${elementId}`, 'put', body),
			getNotificationSubscriptions: (elementId: string | number) =>
				APICall<NotificationSubscription[]>(`notificationSubscriptions/${elementId}`, 'get'),

			usageDays: {
				getAll: (elementId: number) =>
					APICall<UsageDay[]>(`consumptionElements/usageDays/${elementId}`),
				insert: (elementId: number, dayNum: number) =>
					APICall<UsageDay[]>(`consumptionElements/usageDays/${elementId}`, 'post', {
						day: dayNum,
						elementId,
					}),
				delete: (id: number) => APICall<number>(`consumptionElements/usageDays/${id}`, 'delete'),
			},
		},
		savingProgram: {
			cases: {
				add: (objectId: number, newElement: any) =>
					APICall<SavingProgramCase>(`objects/${objectId}/savingProgram/cases`, 'put', newElement),
				getByObject: (objectId: number) =>
					APICall<SavingProgramCase[]>(`objects/${objectId}/savingProgram/cases`, 'get'),
				deleteElementCases: (objectId: number, elementId: number) =>
					APICall<number>(
						`objects/${objectId}/savingProgram/cases/fromElement/${elementId}`,
						'delete'
					),
				deleteById: (objectId: number, caseId: number) =>
					APICall<number>(`objects/${objectId}/savingProgram/cases/${caseId}`, 'delete'),
			},
		},

		getConsumption: (
			from: string,
			to: string,
			objectId: number,
			options: {
				grouping?: string
				meteringPointEAN?: string
				synerallObjectId?: number
				synerallContractId?: number
				personalId?: string
				useDatahub: boolean
			}
		) =>
			APICall<ConsumptionData[] | GroupedConsumptionData>(
				`objects/${objectId}/consumption`,
				'post',
				{ from, to, options }
			),
		setConsumptionCache: (
			objectId: number,
			options: {
				grouping?: string
				meteringPointEAN?: string
				synerallObjectId?: number
				personalId?: string
				useDatahub: boolean
			}
		) => APICall<boolean>(`objects/${objectId}/consumptioncache`, 'put', { options }),
		getEnergyReport: (objectId: number | number, from: string, to: string) =>
			APICall<ConsumptionAnalyzis>(`objects/${objectId}/consumptionAnalysis?from=${from}&to=${to}`),
		getConsumptionReport: (objectId: number | number, date: string) =>
			APICall(`objects/${objectId}/consumptionreports/${date}`),
		setRooms: (objectId: number | number, rooms: any[]) =>
			APICall(`objects/${objectId}/rooms`, 'post', rooms),
		deleteRooms: (objectId: number | number) =>
			APICall<number>(`objects/${objectId}/rooms`, 'delete'),
		updateBrainNet: (objectId: number | number, newData: any) =>
			APICall<{ success: boolean }>(`objects/${objectId}/brainNet`, 'post', newData),
		refreshBrain: (objectId: number) =>
			APICall<{ success: boolean }>(`objects/${objectId}/brainNetRefresh`, 'post'),
		deleteBrain: (objectId: number) =>
			APICall<{ success: boolean }>(`objects/${objectId}/brainNet`, 'delete'),
		dataHubAgreements: (personalId: string) =>
			APICall<DHAgreement[]>(`objects/datahubAuthentication`, 'post', { personalId }),
		getByUserId: (userId: number) => APICall<Object[]>(`user/${userId}/objects`),
		getChanges: (userId: number) => APICall<Object[]>(`user/${userId}/objectchanges`),
	},

	synerall: {
		getContract: (contractId: number) =>
			APICall<SynerallContract>(`synerall/contract/${contractId}`),
	},
}

// function createBackendBaseCalls<Type>(url: string) {
// 	return {
// 		add: (object: any): Promise<Type> => APICall(url, 'post', object),

// 		getById: (id: number): Promise<Type> => APICall(`${url}/${id}`),

// 		edit: (id: number, object: any): Promise<Type> => APICall(`${url}/${id}`, 'put', object),

// 		delete: (id: number) => APICall(`${url}/${id}`, 'delete'),
// 	}
// }
